import { queryOptions } from "@tanstack/react-query";

export const BEST_FARES_KEYS = () => ["best-fares"] as const;
export const BFF_SERVICE_BASE_URL =
  "https://jbreststg.jetblue.com/bff-service-v2/";
export type BestFareDateString = {
  amount: number;
  date: string;
  seats: number;
  tax: number;
};
export type BestFareByDate = Omit<BestFareDateString, "date">;
export type BestFaresApiResponse = {
  currencyCode: string;
  outboundFares: BestFareDateString[];
  inboundFares?: BestFareDateString[] | undefined;
};
export type BestFaresRequest = {
  adult: string;
  child: string;
  destination: string;
  fareType: string;
  infant: string;
  month: string;
  origin: string;
  tripType: string;
};
export type BestFaresByDateResponse = {
  currencyCode: string;
  outboundFaresByDate: Record<string, BestFareByDate>;
  inboundFaresByDate?: Record<string, BestFareByDate> | undefined;
};

const TEN_MINUTES = 600_000;
const THIRTY_MINUTES = 1_800_000;

export const getBestFaresFromApi = async (request: BestFaresRequest) => {
  const url = new URL("bestFares/", BFF_SERVICE_BASE_URL);
  url.search = new URLSearchParams(request).toString();

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch best fares data: ${response.statusText}`);
  }

  const data = (await response.json()) as BestFaresApiResponse;
  return normalizeBestFares(data);
};
export const BEST_FARES_QUERY_KEYS = ({
  origin,
  destination,
  fareType,
  month,
  tripType,
  adult,
  child,
  infant,
}: BestFaresRequest) =>
  [
    ...BEST_FARES_KEYS(),
    origin,
    destination,
    fareType,
    month,
    tripType,
    adult,
    child,
    infant,
  ] as const;
export const bestFaresOptions = (request: BestFaresRequest) =>
  queryOptions({
    gcTime: THIRTY_MINUTES,
    queryFn: () => getBestFaresFromApi(request),
    queryKey: BEST_FARES_QUERY_KEYS(request),
    staleTime: TEN_MINUTES,
  });
export const normalizeBestFares = ({
  currencyCode,
  outboundFares,
  inboundFares,
}: BestFaresApiResponse): BestFaresByDateResponse => {
  const outboundFaresByDate: Record<string, BestFareByDate> = {};
  for (const { date, amount, tax, seats } of outboundFares) {
    outboundFaresByDate[date] = { amount, seats, tax };
  }

  let inboundFaresByDate: Record<string, BestFareByDate> | undefined;
  if (inboundFares) {
    inboundFaresByDate = {};
    for (const { date, amount, tax, seats } of inboundFares) {
      inboundFaresByDate[date] = { amount, seats, tax };
    }
  }

  return {
    currencyCode,
    inboundFaresByDate,
    outboundFaresByDate,
  };
};
